import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router';
import { paths } from 'src/routes/paths';
import { useLpEventsStore } from 'src/store/benefits/lp-events.store';
import { LpEvent } from 'src/types/lp-events';
import * as Yup from 'yup';

export default function useLpEventsDetails() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isEdit = pathname.includes('edit');

  const STATUS_OPTIONS = ['active', 'inactive'];
  const STATUS_LABEL = ['Ativo', 'Inativo'];

  const { enqueueSnackbar } = useSnackbar();

  const {
    getBusinessUnits,
    getLpEventDetails,
    resetLpEventDetails,
    createLpEvent,
    updateLpEvent,
    isLoadingLpEventDetails,
    businessUnits,
    lpEventDetails,
  } = useLpEventsStore();

  const { id } = useParams();

  useEffect(() => {
    const fetchBusinessUnits = async () => {
      try {
        await getBusinessUnits();
      } catch (error) {
        console.error('Error fetching business units:', error);
      }
    };

    fetchBusinessUnits();
  }, []);

  useEffect(() => {
    const fetchDetails = async () => {
      if (id) {
        await getLpEventDetails(id);
      } else {
        resetLpEventDetails();
      }
    };

    fetchDetails();
  }, [isEdit]);

  const defaultValues = {
    name: lpEventDetails?.name || '',
    URLName: lpEventDetails?.URLName || '',
    startDate: lpEventDetails?.startDate ? new Date(lpEventDetails.startDate) : new Date(),
    endDate: lpEventDetails?.endDate ? new Date(lpEventDetails.endDate) : new Date(),
    businessUnitId: lpEventDetails?.businessUnitId || '',
    GAPropertyId: lpEventDetails?.GAPropertyId || '',
    GAPropertySecret: lpEventDetails?.GAPropertySecret || '',
    customContent: lpEventDetails?.customContent || {
      mainBanners: {
        bgColor: lpEventDetails?.customContent?.mainBanners?.bgColor || '#000000',
        textColor: lpEventDetails?.customContent?.mainBanners?.textColor || '#FFFFFF',
        buttonColor: lpEventDetails?.customContent?.mainBanners?.buttonColor || '#FF0000',
        buttonTextColor: lpEventDetails?.customContent?.mainBanners?.buttonTextColor || '#FFFFFF',
      },
      primaryButtons: {
        bgColor: lpEventDetails?.customContent?.primaryButtons?.bgColor || '#000000',
        textColor: lpEventDetails?.customContent?.primaryButtons?.textColor || '#FFFFFF',
      },
      loginPage: {
        bannerImage: lpEventDetails?.customContent?.loginPage?.bannerImage || '',
        contentImage: lpEventDetails?.customContent?.loginPage?.contentImage || '',
        bannerText:
          lpEventDetails?.customContent?.loginPage?.bannerText || 'Resgate benefícios exclusivos!',
        contentText: {
          primaryText:
            lpEventDetails?.customContent?.loginPage?.contentText?.primaryText ||
            'Viva experiências únicas com a Cidade Center Norte',
          secondaryText:
            lpEventDetails?.customContent?.loginPage?.contentText?.secondaryText ||
            'Aproveite todas as vantagens da campanha de inverno.',
        },
        cardsText: {
          upperLeft:
            lpEventDetails?.customContent?.loginPage?.cardsText?.upperLeft ||
            'Cadastro fácil e rápido!',
          upperRight:
            lpEventDetails?.customContent?.loginPage?.cardsText?.upperRight ||
            'Use seus benefícios agora mesmo!',
          bottomLeft:
            lpEventDetails?.customContent?.loginPage?.cardsText?.bottomLeft ||
            'Cupons e descontos imperdíveis.',
          bottomRight:
            lpEventDetails?.customContent?.loginPage?.cardsText?.bottomRight ||
            'Somente nas lojas participantes!',
        },
      },
      registerPage: {
        bannerImage: lpEventDetails?.customContent?.registerPage?.bannerImage || '',
        contentImage: lpEventDetails?.customContent?.registerPage?.contentImage || '',
        bannerText:
          lpEventDetails?.customContent?.registerPage?.bannerText ||
          'Seja bem-vindo(a)! Cadastre-se e aproveite.',
        contentText: {
          primaryText:
            lpEventDetails?.customContent?.registerPage?.contentText?.primaryText ||
            'Sua experiência começa aqui',
          secondaryText:
            lpEventDetails?.customContent?.registerPage?.contentText?.secondaryText ||
            'Preencha seus dados e aproveite os benefícios.',
        },
        cardsText: {
          upperLeft:
            lpEventDetails?.customContent?.registerPage?.cardsText?.upperLeft ||
            'Cadastro simples e gratuito!',
          upperRight:
            lpEventDetails?.customContent?.registerPage?.cardsText?.upperRight ||
            'Resgate seus itens preferidos!',
          bottomLeft:
            lpEventDetails?.customContent?.registerPage?.cardsText?.bottomLeft ||
            'Descontos nas melhores lojas.',
          bottomRight:
            lpEventDetails?.customContent?.registerPage?.cardsText?.bottomRight ||
            'Só na Cidade Center Norte!',
        },
      },
      homePage: {
        bannerImage: lpEventDetails?.customContent?.homePage?.bannerImage || '',
        bannerText: {
          specialColor:
            lpEventDetails?.customContent?.homePage?.bannerText?.specialColor || '#0055AA',
          titleText:
            lpEventDetails?.customContent?.homePage?.bannerText?.titleText ||
            'Seus benefícios estão aqui!',
          items: {
            item1: lpEventDetails?.customContent?.homePage?.bannerText?.items?.item1 || {
              cardText: 'ESCOLHA',
              text: 'Navegue pelo catálogo e escolha seus itens.',
            },
            item2: lpEventDetails?.customContent?.homePage?.bannerText?.items?.item2 || {
              cardText: 'RESGATE',
              text: 'Finalize os resgates e acompanhe em Meus Benefícios.',
            },
            item3: lpEventDetails?.customContent?.homePage?.bannerText?.items?.item3 || {
              cardText: 'APROVEITE',
              text: 'Use seus cupons e viva experiências incríveis.',
            },
          },
          infoText:
            lpEventDetails?.customContent?.homePage?.bannerText?.infoText ||
            'Ofertas exclusivas para clientes cadastrados. Promoções válidas enquanto durarem os estoques.',
        },
        contentText: {
          primaryText:
            lpEventDetails?.customContent?.homePage?.contentText?.primaryText ||
            'Tudo que você precisa em um só lugar',
          secondaryText:
            lpEventDetails?.customContent?.homePage?.contentText?.secondaryText ||
            'Benefícios, descontos, cupons e muito mais esperando por você.',
        },
      },
      myBenefitsPage: {
        titleText:
          lpEventDetails?.customContent?.myBenefitsPage?.titleText ||
          'Confira seus resgates e aproveite!',
      },
    },
    status: lpEventDetails?.status || 'active',
  };

  const lpEventSchema = Yup.object().shape({
    name: Yup.string().required('Campo obrigatório!'),
    URLName: Yup.string().required('Campo obrigatório!'),
    startDate: Yup.date().required('Campo obrigatório!').nullable(),
    endDate: Yup.date()
      .required('Campo obrigatório!')
      .nullable()
      .min(Yup.ref('startDate'), 'A data de fim deve ser posterior à data de início'),
    businessUnitId: Yup.string().required('Campo obrigatório!'),
    GAPropertyId: Yup.string().required('Campo obrigatório!'),
    GAPropertySecret: Yup.string().when([], {
      is: () => !isEdit, // se NÃO está editando, é obrigatório
      then: (schema) => schema.required('Campo obrigatório!'),
      otherwise: (schema) => schema.notRequired(),
    }),
    status: Yup.string()
      .oneOf(['active', 'inactive'], 'Status inválido')
      .required('Campo obrigatório!'),
    customContent: Yup.object().shape({
      mainBanners: Yup.object().shape({
        bgColor: Yup.string().required('Cor obrigatória'),
        textColor: Yup.string().required('Cor obrigatória'),
        buttonColor: Yup.string().required('Cor obrigatória'),
        buttonTextColor: Yup.string().required('Cor obrigatória'),
      }),
      primaryButtons: Yup.object().shape({
        bgColor: Yup.string().required('Cor obrigatória'),
        textColor: Yup.string().required('Cor obrigatória'),
      }),
      loginPage: Yup.object().shape({
        bannerImage: Yup.mixed().test(
          'required',
          'Imagem obrigatória',
          (value) =>
            !!value && (typeof value === 'string' ? value.trim() !== '' : value instanceof File)
        ),
        contentImage: Yup.mixed().test(
          'required',
          'Imagem obrigatória',
          (value) =>
            !!value && (typeof value === 'string' ? value.trim() !== '' : value instanceof File)
        ),
        bannerText: Yup.string().required('Texto obrigatório'),
        contentText: Yup.object().shape({
          primaryText: Yup.string().required('Texto obrigatório'),
          secondaryText: Yup.string().required('Texto obrigatório'),
        }),
        cardsText: Yup.object().shape({
          upperLeft: Yup.string().required('Texto obrigatório'),
          upperRight: Yup.string().required('Texto obrigatório'),
          bottomLeft: Yup.string().required('Texto obrigatório'),
          bottomRight: Yup.string().required('Texto obrigatório'),
        }),
      }),
      registerPage: Yup.object().shape({
        bannerImage: Yup.mixed().test(
          'required',
          'Imagem obrigatória',
          (value) =>
            !!value && (typeof value === 'string' ? value.trim() !== '' : value instanceof File)
        ),
        contentImage: Yup.mixed().test(
          'required',
          'Imagem obrigatória',
          (value) =>
            !!value && (typeof value === 'string' ? value.trim() !== '' : value instanceof File)
        ),
        bannerText: Yup.string().required('Texto obrigatório'),
        contentText: Yup.object().shape({
          primaryText: Yup.string().required('Texto obrigatório'),
          secondaryText: Yup.string().required('Texto obrigatório'),
        }),
        cardsText: Yup.object().shape({
          upperLeft: Yup.string().required('Texto obrigatório'),
          upperRight: Yup.string().required('Texto obrigatório'),
          bottomLeft: Yup.string().required('Texto obrigatório'),
          bottomRight: Yup.string().required('Texto obrigatório'),
        }),
      }),
      homePage: Yup.object().shape({
        bannerImage: Yup.mixed().test(
          'required',
          'Imagem obrigatória',
          (value) =>
            !!value && (typeof value === 'string' ? value.trim() !== '' : value instanceof File)
        ),
        bannerText: Yup.object().shape({
          specialColor: Yup.string().required('Cor obrigatória'),
          titleText: Yup.string().required('Texto obrigatório'),
          items: Yup.object().shape({
            item1: Yup.object().shape({
              cardText: Yup.string().required('Texto obrigatório'),
              text: Yup.string().required('Texto obrigatório'),
            }),
            item2: Yup.object().shape({
              cardText: Yup.string().required('Texto obrigatório'),
              text: Yup.string().required('Texto obrigatório'),
            }),
            item3: Yup.object().shape({
              cardText: Yup.string().required('Texto obrigatório'),
              text: Yup.string().required('Texto obrigatório'),
            }),
          }),
          infoText: Yup.string().required('Texto obrigatório'),
        }),
        contentText: Yup.object().shape({
          primaryText: Yup.string().required('Texto obrigatório'),
          secondaryText: Yup.string().required('Texto obrigatório'),
        }),
      }),
      myBenefitsPage: Yup.object().shape({
        titleText: Yup.string().required('Título obrigatório'),
      }),
    }),
  });

  const methods = useForm({
    resolver: yupResolver(lpEventSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { isSubmitting },
  } = methods;

  const formValues = watch();

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [reset, lpEventDetails]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      const formattedData = {
        ...data,
        _id: id,
      };

      if (isEdit && !formattedData.GAPropertySecret) {
        delete formattedData.GAPropertySecret;
      }

      if (isEdit && id) {
        await updateLpEvent(id, formattedData as LpEvent);
        enqueueSnackbar({ message: 'Evento editado com sucesso.', variant: 'success' });
      } else {
        await createLpEvent(data as LpEvent);
        enqueueSnackbar({ message: 'Evento criado com sucesso.', variant: 'success' });
      }
      navigate(paths.dashboard.lpEventsList);
    } catch (error) {
      enqueueSnackbar({ message: error.message, variant: 'error' });
    }
  });

  const handleDropBannerImage = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      if (!file) return;

      const reader = new FileReader();

      reader.onload = () => {
        const img = new Image();

        img.src = reader.result as string;

        img.onload = () => {
          setValue(
            'customContent.loginPage.bannerImage',
            Object.assign(file, {
              preview: reader.result,
            })
          );
        };
      };

      reader.readAsDataURL(file);
    },
    [setValue]
  );

  const handleDropContentImage = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      if (!file) return;

      const reader = new FileReader();

      reader.onload = () => {
        const img = new Image();

        img.src = reader.result as string;

        img.onload = () => {
          setValue(
            'customContent.loginPage.contentImage',
            Object.assign(file, {
              preview: reader.result,
            })
          );
        };
      };

      reader.readAsDataURL(file);
    },
    [setValue]
  );

  const handleDropRegisterPageBannerImage = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      if (!file) return;

      const reader = new FileReader();

      reader.onload = () => {
        const img = new Image();

        img.src = reader.result as string;

        img.onload = () => {
          setValue(
            'customContent.registerPage.bannerImage',
            Object.assign(file, {
              preview: reader.result,
            })
          );
        };
      };

      reader.readAsDataURL(file);
    },
    [setValue]
  );

  const handleDropRegisterPageContentImage = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      if (!file) return;

      const reader = new FileReader();

      reader.onload = () => {
        const img = new Image();

        img.src = reader.result as string;

        img.onload = () => {
          setValue(
            'customContent.registerPage.contentImage',
            Object.assign(file, {
              preview: reader.result,
            })
          );
        };
      };

      reader.readAsDataURL(file);
    },
    [setValue]
  );

  const handleDropHomePageBannerImage = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      if (!file) return;

      const reader = new FileReader();

      reader.onload = () => {
        const img = new Image();

        img.src = reader.result as string;

        img.onload = () => {
          setValue(
            'customContent.homePage.bannerImage',
            Object.assign(file, {
              preview: reader.result,
            })
          );
        };
      };

      reader.readAsDataURL(file);
    },
    [setValue]
  );

  return {
    formValues,
    isEdit,
    isLoadingLpEventDetails,
    isSubmitting,
    control,
    methods,
    businessUnits,
    lpEventDetails,
    STATUS_OPTIONS,
    STATUS_LABEL,
    handleDropBannerImage,
    handleDropContentImage,
    handleDropRegisterPageBannerImage,
    handleDropRegisterPageContentImage,
    handleDropHomePageBannerImage,
    navigate,
    onSubmit,
  };
}
